import { Box, Chip, Divider, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../components/container/TableHeaderToolsContainer";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsClock } from "react-icons/bs";
import EnvironmentFactorGrid from "../components/grid/EnvironmentFactorGrid";
import ProcessGrid from "../components/grid/ProcessGrid";
import { useContextProvider } from "../components/page-context/ContextProvider";
import SecondaryTabs from "../components/tabs/SecondaryTabs";
import { EnvFactor } from "../db_access/env_factor";
import { Process } from "../db_access/process";
import { Product } from "../db_access/product";
import muiSytle from "../style/components-style/muiStyle";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";

const LifeCycleInventory = ({ title = "Life Cycle Inventory", selectedID, isSupplier, isReuse, buildingSection = null, onClearBuildingSection = null, reusedID }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { refresh, setIsSaveClicked, isSaveVisible, selectedBuildings, selectedProjects, selectedProducts } = useContextProvider();
  const [selectedTab, setSelectedTab] = useState("All");
  const [processes, setProcesses] = useState([]);
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [envFactors, setEnvFactors] = useState([]);
  const [supplierName, setSupplierName] = useState("");
  const [productName, setProductName] = useState("");

  const getIsRecordLocked = () => {
    if (selectedBuildings) {
      const b = selectedBuildings.find(item => item.id === selectedID);
      if (b?.validation_status === "Pending" || b?.validation_status === "Validated") return true;
    }
    if (selectedProjects) {
      const pr = selectedProjects.find(item => item.id === selectedID);
      if (pr?.validation_status === "Pending" || pr?.validation_status === "Validated") return true;
    }
    if (selectedProducts) {
      const p = selectedProducts.find(item => item.id === selectedID);
      if (p?.validation_status === "Pending" || p?.validation_status === "Validated") return true;
    }
    return false;
  };
  const isLocked = getIsRecordLocked();
  const [autoSaveInterval, setAutoSaveInterval] = useState(0);
  const isBuildingMode = Boolean(buildingSection);

  // Auto save interval options: Off (0), 30s (30000), 1min (60000), 5min (300000)
  const autoSaveOptions = [
    { value: 0, label: "Auto Save: Off" },
    { value: 30000, label: "Auto Save: 30s" },
    { value: 60000, label: "Auto Save: 1min" },
    { value: 300000, label: "Auto Save: 5min" },
  ];

  const handleAutoSaveClick = () => {
    const currentIndex = autoSaveOptions.findIndex((option) => option.value === autoSaveInterval);
    const nextIndex = (currentIndex + 1) % autoSaveOptions.length;
    setAutoSaveInterval(autoSaveOptions[nextIndex].value);
  };

  const getCurrentAutoSaveLabel = () => {
    const option = autoSaveOptions.find((opt) => opt.value === autoSaveInterval);
    return option ? option.label : "Auto Save: Off";
  };

  useEffect(() => {
    if (autoSaveInterval > 0) {
      const interval = setInterval(() => {
        setIsSaveClicked(true);
        // Reset the save button state after a short delay to allow save to complete
        setTimeout(() => {
          setIsSaveClicked(false);
        }, 500);
      }, autoSaveInterval);
      return () => clearInterval(interval);
    }
  }, [autoSaveInterval, setIsSaveClicked]);

  const filterTabs = useMemo(() => {
    return isSupplier && !isReuse
      ? [
        { label: "All", filter: (item) => ["A1", "A2", "A3"].some((prefix) => item.lca_stage.startsWith(prefix)), value: "All" },
        { label: "A1", filter: (item) => item.lca_stage.startsWith("A1"), value: "A1" },
        { label: "A2", filter: (item) => item.lca_stage.startsWith("A2"), value: "A2" },
        { label: "A3", filter: (item) => item.lca_stage.startsWith("A3"), value: "A3" },
      ]
      : [
        { label: "All", filter: () => true, value: "All" },
        { label: "Product", filter: (item) => ["A1", "A2", "A3"].some((prefix) => item.lca_stage.startsWith(prefix)), value: "A1-A3" },
        { label: "Construction", filter: (item) => ["A4", "A5"].some((prefix) => item.lca_stage.startsWith(prefix)), value: "A4-A5" },
        { label: "Use", filter: (item) => item.lca_stage.startsWith("B"), value: "B" },
        { label: "End-of-life", filter: (item) => item.lca_stage.startsWith("C"), value: "C" },
        { label: "Benefits", filter: (item) => item.lca_stage.startsWith("D"), value: "D" },
      ];
  }, [isSupplier, isReuse]);

  useEffect(() => {
    let ignore = false;
    if (!selectedID) {
      setProcesses([]);
      return;
    }
    // Smart Refresh: If there are unsaved changes (isSaveVisible),
    // skip the refresh to avoid overwriting pending edits.
    if (isSaveVisible) return;

    const request = isSupplier ? Process.get_by_product_ids : isBuildingMode ? Process.get_by_building_ids : Process.get_by_project_ids;
    request([selectedID])
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (ignore) return;
        let filteredData = data;
        if (isBuildingMode && buildingSection) {
          filteredData = data.filter((p) => {
            const catMatch =
              String(p.building_section_category || "").toLowerCase() ===
              String(buildingSection.categoryId || "").toLowerCase();
            if (!buildingSection.subcategoryId) return catMatch;
            return (
              catMatch &&
              String(p.building_section_subcategory || "").toLowerCase() ===
                String(buildingSection.subcategoryId || "").toLowerCase()
            );
          });
        }

        const sortedData = filteredData.sort((a, b) => {
          const dateA = a.date_modified ? new Date(a.date_modified).getTime() : Infinity;
          const dateB = b.date_modified ? new Date(b.date_modified).getTime() : Infinity;
          return dateA - dateB;
        });
        setProcesses(sortedData.map((i) => ({ ...i, _isModified: false })));

        if (isReuse && isSupplier) {
          // set the processes get from product as read-only
          setProcesses((prev) => prev.map((p) => ({ ...p, readOnly: true })));
          if (!reusedID) return;
          // fetch from reused_product processes and append
          Process.get_by_reused_product_id(reusedID)
            .then((res) => res.json())
            .then((data) => {
              if (ignore) return;
              setProcesses((prev) => [...prev, ...data.map((i) => ({ ...i, _isModified: false }))]);
            });
        }
      })
      .catch((error) => {
        if (ignore) return;
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Error fetching data:", error.message);
        }
      });

    return () => {
      ignore = true;
    };
  }, [selectedID, refresh, isSaveVisible, isSupplier, isBuildingMode, buildingSection, isReuse, reusedID]);

  useEffect(() => {
    setSelectedProcessIds([]);
  }, [selectedID]);

  // Default selection: automatically select A1 stage process when processes load
  useEffect(() => {
    if (processes.length > 0 && selectedProcessIds.length === 0) {
      const a1Process = processes.find(
        (p) => p.lca_stage && p.lca_stage.trim().toLowerCase().startsWith("a1")
      );
      if (a1Process) {
        setSelectedProcessIds([a1Process.id]);
      } else {
        setSelectedProcessIds([processes[0].id]);
      }
    }
  }, [processes, selectedProcessIds]);

  useEffect(() => {
    const process_ids = Array.from(new Set(processes.map((process) => process?.id).filter((id) => id != null)));
    if (process_ids.length === 0) {
      setEnvFactors([]);
      return;
    }
    EnvFactor.get_by_process_ids(process_ids)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((results) => {
        const sortedResults = results.sort((a, b) => {
          const rowA = a.source_row != null ? parseInt(a.source_row, 10) : 999999;
          const rowB = b.source_row != null ? parseInt(b.source_row, 10) : 999999;
          if (rowA !== rowB) return rowA - rowB;

          const dateA = a.date_modified ? new Date(a.date_modified).getTime() : Infinity;
          const dateB = b.date_modified ? new Date(b.date_modified).getTime() : Infinity;
          return dateA - dateB;
        });
        setEnvFactors(sortedResults.map((i) => ({ ...i })));
      });
  }, [processes]);

  useEffect(() => {
    if (!isSupplier) {
      return;
    }
    if (!selectedID) {
      return;
    }
    Product.get_supplier_and_product_by_id(selectedID)
      .then((res) => res.json())
      .then((data) => {
        if (data.length === 1) {
          setProductName(data[0]["product"]);
          setSupplierName(data[0]["supplier"]);
        }
      });
  }, [selectedID, isSupplier]);

  useEffect(() => {
    setSelectedTab(filterTabs[0].value);
  }, [isSupplier, isReuse, filterTabs]);

  const processToolsLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: 2 }}>
      <Box sx={{
        display: "flex",
        bgcolor: isDark ? ORG_PALETTE.segmentedTabs.containerBgDk : "#f1f5f9",
        p: 0.5,
        borderRadius: "10px",
      }}>
        {(filterTabs || []).map((tab) => {
          const isActive = selectedTab === tab.value;
          return (
            <Box
              key={tab.value}
              onClick={() => setSelectedTab(tab.value)}
              sx={{
                px: 3,
                py: 1,
                flex: 1,
                minWidth: "60px",
                fontSize: "13px",
                fontWeight: 600,
                borderRadius: "8px",
                cursor: "pointer",
                color: isActive ? "#fff" : (isDark ? ORG_PALETTE.segmentedTabs.inactiveColorDk : "#64748b"),
                bgcolor: isActive ? "#659d33" : "transparent",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                whiteSpace: "nowrap",
                boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                "&:hover": {
                  bgcolor: isActive ? "#659d33" : (isDark ? ORG_PALETTE.segmentedTabs.hoverBgDk : "rgba(0,0,0,0.02)")
                }
              }}
            >
              {t(tab.label)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t(title)}</Typography>
          {buildingSection && (
            <Chip
              label={
                buildingSection.subcategoryName
                  ? `${buildingSection.categoryName} → ${buildingSection.subcategoryName}`
                  : `${buildingSection.categoryName} (All)`
              }
              onDelete={onClearBuildingSection || undefined}
              sx={{
                backgroundColor: isDark ? "rgba(22, 163, 74, 0.2)" : "#dcfce7",
                color: isDark ? "#4ade80" : "#166534",
                fontWeight: 500,
                fontFamily: "Poppins",
                fontSize: "13px",
                height: "28px",
                "& .MuiChip-deleteIcon": {
                  color: isDark ? "#4ade80" : "#166534",
                },
              }}
            />
          )}
        </TableTitleContainer>
        <TableHeaderToolsContainer>
          <Chip
            icon={<BsClock style={{ fontSize: "16px" }} />}
            label={getCurrentAutoSaveLabel()}
            onClick={handleAutoSaveClick}
            sx={{
              fontFamily: "Poppins",
              fontSize: "13px",
              fontWeight: 600,
              height: "40px",
              borderRadius: "10px",
              backgroundColor: autoSaveInterval > 0 ? "rgba(101, 157, 51, 0.15)" : tok.bg.tabContainer,
              color: autoSaveInterval > 0 ? (isDark ? "#82b94c" : "#659d33") : tok.text.secondary,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: autoSaveInterval > 0 ? "rgba(101, 157, 51, 0.2)" : isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
              "& .MuiChip-icon": {
                color: autoSaveInterval > 0 ? (isDark ? "#82b94c" : "#659d33") : tok.text.secondary,
                marginLeft: "8px",
              },
              "& .MuiChip-label": {
                paddingLeft: "8px",
                paddingRight: "12px",
              },
            }}
          />
        </TableHeaderToolsContainer>
      </TableHeaderContainer>
      <Box sx={{ display: "flex", flexDirection: "column", pb: 3 }}>
        <ProcessGrid
          headerToolsLeft={processToolsLeft}
          processes={processes.filter(filterTabs.find((tab) => tab.value === selectedTab).filter)}
          setProcesses={setProcesses}
          selectedProcessIds={selectedProcessIds}
          setSelectedProcessIds={setSelectedProcessIds}
          isSupplier={isSupplier}
          editable={!isLocked}
          productID={isSupplier && !isReuse ? selectedID : null}
          projectID={!isSupplier && !isBuildingMode ? selectedID : null}
          buildingID={isBuildingMode ? selectedID : null}
          reusedProductID={isSupplier && isReuse ? reusedID : null}
          buildingSection={buildingSection}
          isReuse={isReuse}
          maxHeight="400px"
        />

        <Box sx={{ mt: 3, borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)' }}>
          <EnvironmentFactorGrid
            envFactors={envFactors}
            isSupplier={isSupplier}
            editable={isLocked ? false : !processes.find((p) => p.id === selectedProcessIds[0])?.readOnly}
            processID={selectedProcessIds.length === 1 ? selectedProcessIds[0] : undefined}
            selectedProcessIds={selectedProcessIds}
            supplier={supplierName}
            product={productName}
            productID={isSupplier && !isReuse ? selectedID : null}
            projectID={!isSupplier && !isBuildingMode ? selectedID : null}
            buildingID={isBuildingMode ? selectedID : null}
            maxHeight="600px"
            lcaStage={processes.find((p) => p.id === selectedProcessIds[0])?.lca_stage}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default LifeCycleInventory;
